import React, { useEffect, useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { AnimatePresence, MotiView } from "moti";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors } from "../../../../../styles/design-system";

type TabKey = "review" | "attachments" | "manualSteps";

interface PanelTabsProps {
  attachmentsCount: number;
  reviewContent: React.ReactNode;
  attachmentsContent: React.ReactNode;
  manualStepsCount?: number;
  manualStepsContent?: React.ReactNode;
}

function PanelTabs({
  attachmentsCount,
  reviewContent,
  attachmentsContent,
  manualStepsCount = 0,
  manualStepsContent,
}: PanelTabsProps) {
  const [active, setActive] = useState<TabKey>("review");

  useEffect(() => {
    if (active === "manualSteps" && manualStepsCount === 0) {
      setActive("review");
    }
  }, [active, manualStepsCount]);

  const tablistLabel = "Review content tabs";

  const renderContent = () => {
    switch (active) {
      case "attachments":
        return attachmentsContent;
      case "manualSteps":
        return manualStepsContent;
      default:
        return reviewContent;
    }
  };

  return (
    <View>
      <View
        style={styles.tabBar}
        accessibilityRole="tablist"
        accessibilityLabel={tablistLabel}
        role="tablist"
        aria-label={tablistLabel}>
        <TabButton label="Review" isActive={active === "review"} onPress={() => setActive("review")} />
        <TabButton
          label={`Attachments (${attachmentsCount})`}
          isActive={active === "attachments"}
          onPress={() => setActive("attachments")}
        />
        {manualStepsCount > 0 && (
          <TabButton
            label={`Manual Steps (${manualStepsCount})`}
            isActive={active === "manualSteps"}
            onPress={() => setActive("manualSteps")}
          />
        )}
      </View>

      <AnimatePresence exitBeforeEnter>
        <MotiView
          key={active}
          from={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ type: "timing", duration: 120 }}>
          {renderContent()}
        </MotiView>
      </AnimatePresence>
    </View>
  );
}

interface TabButtonProps {
  label: string;
  isActive: boolean;
  onPress: () => void;
}

function TabButton({ label, isActive, onPress }: TabButtonProps) {
  return (
    <Pressable
      onPress={onPress}
      style={[styles.tabButton, isActive ? styles.tabButtonActive : styles.tabButtonInactive]}
      accessibilityRole="tab"
      accessibilityState={{ selected: isActive }}
      accessibilityLabel={label}
      role="tab"
      aria-selected={isActive}
      aria-label={label}>
      <CustomText
        text={label}
        useTranslationText={false}
        size="s"
        weight="medium"
        style={isActive ? styles.tabTextActive : styles.tabTextInactive}
      />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  tabBar: {
    flexDirection: "row",
    gap: 24,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    marginBottom: 16,
  },
  tabButton: {
    paddingVertical: 10,
    borderBottomWidth: 2,
  },
  tabButtonActive: {
    borderBottomColor: colors.brand[600],
  },
  tabButtonInactive: {
    borderBottomColor: "transparent",
  },
  tabTextActive: {
    color: colors.brand[600],
  },
  tabTextInactive: {
    color: colors.grey[600],
  },
});

export default PanelTabs;
